<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <v-card flat class="mt-4 mb-4">
          <v-card-title>
            <h3 class="headline">Pipelines</h3>
          </v-card-title>
          <v-card-text>
            <v-data-table
              :headers="headers"
              :items="items"
              :items-per-page="itemsPerPage"
              :search="search"
              class="elevation-1"
            >
              <template v-slot:top>
                <v-toolbar flat>
                  <v-text-field
                    v-model="search"
                    append-icon="mdi-magnify"
                    label="Search"
                    single-line
                    hide-details
                  ></v-text-field>
                  <v-spacer></v-spacer>
                  <v-btn
                    color="primary"
                    dark
                    class="mb-2"
                    @click="$router.push({ name: 'PipelineNew' }, () => {})"
                  >
                    New pipeline
                  </v-btn>

                  <!-- Cancel pipeline dialog -->
                  <v-dialog v-model="dialogDelete" max-width="500px">
                    <v-card>
                      <v-card-title class="text-h5"
                        >Are you sure you want to cancel job
                        {{ editedItem.id }}?</v-card-title
                      >
                      <v-card-text>
                        <v-list-item>
                          <v-list-item-content>
                            <v-list-item-title>Name</v-list-item-title
                            >{{ editedItem.name }}
                          </v-list-item-content>
                        </v-list-item>
                        <v-list-item>
                          <v-list-item-content>
                            <v-list-item-title>Dataset</v-list-item-title
                            >{{ editedItem.dataset }}
                          </v-list-item-content>
                        </v-list-item>
                      </v-card-text>

                      <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn color="blue darken-1" text @click="stopItem"
                          >Yes</v-btn
                        >
                        <v-btn color="blue darken-1" text @click="closeDelete"
                          >No</v-btn
                        >
                        <v-spacer></v-spacer>
                      </v-card-actions>
                    </v-card>
                  </v-dialog>

                  <!-- Restart pipeline dialog -->
                  <v-dialog v-model="dialogRestart" max-width="500px">
                    <v-card>
                      <v-card-title class="text-h5"
                        >Are you sure you want to restart job
                        {{ editedItem.id }}?</v-card-title
                      >
                      <v-card-text>
                        <v-list-item>
                          <v-list-item-content>
                            <v-list-item-title>Name</v-list-item-title
                            >{{ editedItem.name }}
                          </v-list-item-content>
                        </v-list-item>
                        <v-list-item>
                          <v-list-item-content>
                            <v-list-item-title>Dataset</v-list-item-title
                            >{{ editedItem.dataset }}
                          </v-list-item-content>
                        </v-list-item>
                      </v-card-text>

                      <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn color="blue darken-1" text @click="restartItem"
                          >Yes</v-btn
                        >
                        <v-btn color="blue darken-1" text @click="closeRestart"
                          >No</v-btn
                        >
                        <v-spacer></v-spacer>
                      </v-card-actions>
                    </v-card>
                  </v-dialog>

                  <!-- Pipeline info dialog -->
                  <v-dialog
                    v-model="dialogInfo"
                    fullscreen
                    hide-overlay
                    transition="dialog-bottom-transition"
                  >
                    <v-toolbar dark color="primary">
                      <v-btn icon dark @click="dialogInfo = false">
                        <v-icon>mdi-close</v-icon>
                      </v-btn>
                      <v-toolbar-title>
                        Job {{ editedItem.id }}
                        <v-chip
                          class="ml-2"
                          :color="getColor(editedItem.status)"
                          dark
                          >{{ editedItem.status }}</v-chip
                        >
                      </v-toolbar-title>
                    </v-toolbar>
                    <v-card>
                      <v-card-text>
                        <v-tabs>
                          <v-tab>Summary</v-tab>
                          <v-tab>Configuration</v-tab>
                          <v-tab>Logs</v-tab>
                          <v-tab>Graphs</v-tab>

                          <v-tab-item>
                            <v-list-item two-line>
                              <v-list-item-content>
                                <v-list-item-title>Pipeline</v-list-item-title>
                                <v-list-item-subtitle>{{
                                  editedItem.name
                                }}</v-list-item-subtitle>
                              </v-list-item-content>
                            </v-list-item>
                            <v-list-item two-line>
                              <v-list-item-content>
                                <v-list-item-title>Dataset</v-list-item-title>
                                <v-list-item-subtitle>{{
                                  editedItem.dataset
                                }}</v-list-item-subtitle>
                              </v-list-item-content>
                            </v-list-item>
                            <v-list-item two-line>
                              <v-list-item-content>
                                <v-list-item-title>Resources</v-list-item-title>
                                <v-list-item-subtitle>
                                  CPU: {{ config.resources.cpu }} | Memory:
                                  {{ config.resources.memory }} | Workers:
                                  {{ config.resources.workers }}
                                </v-list-item-subtitle>
                              </v-list-item-content>
                            </v-list-item>
                            <v-list-item two-line>
                              <v-list-item-content>
                                <v-list-item-title>Heartbeat</v-list-item-title>
                                <v-list-item-subtitle>
                                  Started: {{ formatTime(heartbeat.started) }}
                                  <br />
                                  Elapsed:
                                  {{ formatElapsed(heartbeat.elapsed) }}
                                </v-list-item-subtitle>
                              </v-list-item-content>
                            </v-list-item>
                          </v-tab-item>
                          <v-tab-item>
                            <pre class="ma-6">{{
                              json2yaml(editedItem.config)
                            }}</pre>
                          </v-tab-item>
                          <v-tab-item>
                            <log-viewer :jobid="editedItem.id" />
                          </v-tab-item>
                          <v-tab-item>
                            <v-select v-model="chartType" :items="chartTypes" />
                            <chart-viewer
                              :jobid="editedItem.id"
                              title="Memory"
                              metric="memory"
                              :runType="chartType"
                              :url="pipeline_metrics_url"
                            />
                            <chart-viewer
                              :jobid="editedItem.id"
                              title="CPU"
                              metric="cpu"
                              :runType="chartType"
                              :url="pipeline_metrics_url"
                            />
                          </v-tab-item>
                        </v-tabs>
                      </v-card-text>
                    </v-card>
                  </v-dialog>
                </v-toolbar>
              </template>
              <template v-slot:item.status="{ item }">
                <v-chip :color="getColor(item.status)" dark>
                  {{ item.status }}
                </v-chip>
              </template>

              <template v-slot:item.created_on="{ item }">
                {{ formatDate(item.started_at) || formatDate(item.created_on) }}
              </template>

              <template v-slot:item.actions="{ item }">
                <v-icon class="mr-2" @click="infoPipeline(item)">
                  mdi-information-outline
                </v-icon>
                <v-icon
                  v-if="item.status == 'RUNNING'"
                  class="mr-2"
                  @click="stopPipeline(item)"
                >
                  mdi-stop-circle-outline
                </v-icon>
                <v-icon
                  class="mr-2"
                  v-if="item.status != 'RUNNING'"
                  @click="restartPipeline(item)"
                >
                  mdi-reload
                </v-icon>
                <v-icon
                  v-if="item.name === 'imaxt-mosaic'"
                  class="mr-2"
                  @click="previewDataset(item)"
                >
                  mdi-panorama-outline
                </v-icon>
                <v-btn icon :href="getS3Store(item.dataset)">
                  <v-icon v-if="item.name === 'imaxt-mosaic'" class="mr-2">
                    mdi-open-in-app
                  </v-icon>
                </v-btn>

                <!-- <v-icon class="mr-2" @click="infoPipeline(item)">
                  mdi-open-in-new
                </v-icon> -->
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>
  
<script>
import moment from "moment";
import YAML from "yaml";

import ChartViewer from "@/components/ChartViewer.vue";
import LogViewer from "@/components/LogViewer";

import {
  API,
  OWL_PIPELINES,
  OWL_PIPELINE_METRICS,
  OWL_PIPELINE_RESTART,
  OWL_PIPELINE_STOP,
} from "@/axios-config";

export default {
  name: "PipelineList",
  components: {
    LogViewer,
    ChartViewer,
  },
  data() {
    return {
      headers: [
        { text: "Job", value: "id" },
        { text: "Name", value: "name" },
        { text: "Dataset", value: "dataset" },
        { text: "Created", value: "created_on" },
        { text: "Status", value: "status" },
        { text: "Actions", value: "actions", sortable: false },
      ],
      chartTypes: ["Last run", "All runs"],
      chartType: "Last run",
      itemsPerPageArray: [4, 8, 12],
      search: "",
      filter: {},
      sortDesc: false,
      page: 1,
      itemsPerPage: 10,
      sortBy: "name",
      keys: ["Name", "Version"],
      items: [],
      editedIndex: -1,
      dialogDelete: false,
      dialogRestart: false,
      dialogInfo: false,
      editedItem: {},
      config: { resources: {} },
      heartbeat: {},
      logs: [],
      pipeline_metrics_url: OWL_PIPELINE_METRICS,
    };
  },
  computed: {
    numberOfPages() {
      return Math.ceil(this.items.length / this.itemsPerPage);
    },
    filteredKeys() {
      return this.keys.filter((key) => key !== "Name");
    },
  },
  methods: {
    nextPage() {
      if (this.page + 1 <= this.numberOfPages) this.page += 1;
    },
    formerPage() {
      if (this.page - 1 >= 1) this.page -= 1;
    },
    updateItemsPerPage(number) {
      this.itemsPerPage = number;
    },
    getColor(status) {
      if (status == "RUNNING") return "blue";
      else if (status == "ERROR") return "red";
      else if (status == "TO_CANCEL") return "red";
      else if (status == "CANCELLED") return "red";
      else if (status == "FINISHED") return "green";
      else if (status == "PENDING") return "orange";
      else return "white";
    },
    formatDate(date) {
      if (!date) return "";
      return moment(date).format("DD/MM/YYYY HH:mm");
    },
    formatElapsed(value) {
      if (!value) return "";
      if (value < 60) {
        return `${value} s`;
      } else if (value < 3600) {
        return `${Math.floor(value / 60)} min`;
      } else {
        return `${Math.floor(value / 3600)} h`;
      }
    },
    stopPipeline(item) {
      this.editedIndex = this.items.indexOf(item);
      this.editedItem = Object.assign({}, item);
      this.dialogDelete = true;
    },
    restartPipeline(item) {
      this.editedIndex = this.items.indexOf(item);
      this.editedItem = Object.assign({}, item);
      this.dialogRestart = true;
    },
    async restartItem() {
      try {
        await API.get(OWL_PIPELINE_RESTART, {
          params: {
            jobid: parseInt(this.editedItem.id),
          },
        });
        await this.loadPipelines();
      } catch (error) {
        console.log(error);
      }
      this.dialogRestart = false;
    },
    async stopItem() {
      try {
        await API.get(OWL_PIPELINE_STOP, {
          params: {
            jobid: parseInt(this.editedItem.id),
          },
        });
        await this.loadPipelines();
      } catch (error) {
        console.log(error);
      }
      this.dialogDelete = false;
    },
    async infoPipeline(item) {
      this.editedIndex = this.items.indexOf(item);
      this.editedItem = Object.assign({}, item);
      this.config = this.editedItem.config;
      this.heartbeat = this.editedItem.heartbeat;
      this.dialogInfo = true;
    },
    closeDelete() {
      this.dialogDelete = false;
    },
    closeRestart() {
      this.dialogRestart = false;
    },
    json2yaml(json) {
      return YAML.stringify(json);
    },
    formatTime(time) {
      return moment(time * 1000).format("DD/MM/YYYY HH:mm");
    },
    async loadPipelines() {
      try {
        const { data } = await API.get(OWL_PIPELINES);
        this.items = data;
      } catch (error) {
        console.log(error);
      }
    },
    previewDataset(item) {
      console.log(item);
      var instrument = item.config["stitcher"];
      this.$router.push({
        name: "Preview",
        params: { instrumentName: instrument, datasetName: item.dataset },
      });
    },
    getS3Store(item) {
      return "s3://ioa/processed/stpt/" + item;
    },
  },
  mounted: function () {
    this.$nextTick(async function () {
      // Code that will run only after the
      // entire view has been rendered
      // we can run async functions here

      this.loadPipelines();
    });
  },
};
</script>